import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { UserCombobox } from '@/components/common/EntityCombobox';
import { Plus, X } from 'lucide-react';
import { useClientsConfig } from '@/config/clients-config';
import { ClientContactsFormProps } from './types';

export function ClientContactsForm({
  clientContacts,
  onChange,
  availableUsers,
  maxContacts
}: ClientContactsFormProps) {
  const config = useClientsConfig();
  const effectiveMaxContacts = maxContacts || config.maxClientContacts;

  const addContact = () => {
    const availableUser = getNextAvailableUser();
    if (availableUser && clientContacts.length < effectiveMaxContacts) {
      onChange([...clientContacts, availableUser.id]);
    }
  };

  const updateContact = (index: number, userId: number) => {
    const newContacts = [...clientContacts];
    newContacts[index] = userId;
    onChange(newContacts);
  };

  const removeContact = (index: number) => {
    const newContacts = [...clientContacts];
    newContacts.splice(index, 1);
    onChange(newContacts);
  };

  const getNextAvailableUser = () => {
    return availableUsers.find(user => !clientContacts.includes(user.id));
  };

  const getAvailableUsersForSelect = (currentUserId?: number) => {
    return availableUsers.filter(user => 
      !clientContacts.includes(user.id) || user.id === currentUserId
    );
  };

  if (!config.enableClientContacts) {
    return null;
  }

  return (
    <div className="col-span-2 space-y-3">
      <Label className="text-base font-medium">Client Contacts (Internal Users)</Label>
      
      {clientContacts.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No client contacts assigned yet.
        </p>
      )}
      
      {clientContacts.map((contactId, index) => {
        const availableForThisSelect = getAvailableUsersForSelect(contactId);
        
        return (
          <div 
            key={`${contactId}-${index}`} 
            className="flex items-center gap-3 p-3 border rounded-lg bg-muted/20"
          >
            <div className="flex-1">
              <UserCombobox
                value={contactId || null}
                onChange={(id) => id && updateContact(index, id)}
                users={availableForThisSelect}
              />
            </div>
            
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => removeContact(index)}
              title="Remove client contact"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
      
      {clientContacts.length < effectiveMaxContacts && getNextAvailableUser() && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={addContact}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Client Contact
          {effectiveMaxContacts > 1 && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({clientContacts.length}/{effectiveMaxContacts})
            </span>
          )}
        </Button>
      )}
      
      {clientContacts.length >= effectiveMaxContacts && (
        <p className="text-xs text-muted-foreground">
          Maximum number of client contacts reached ({effectiveMaxContacts})
        </p>
      )}
      
      {!getNextAvailableUser() && clientContacts.length < effectiveMaxContacts && (
        <p className="text-xs text-muted-foreground">
          No more users available to assign as client contacts
        </p>
      )}
    </div>
  );
}
